import { useState, KeyboardEvent, useCallback } from 'react';
import { useLoginWithEmail } from '@privy-io/react-auth';
import { notify } from 'src/utils/notifications';

const EMAIL_VALIDATION_REGEX =
	/^(?!\.)(?!.*\.\.)([a-z0-9_'+\-.]*)[a-z0-9_+-]@([a-z0-9][a-z0-9-]*\.)+[a-z]{2,}$/i;

interface Params {
	email: string;
	setEmail: (email: string) => void;
	onLoginWithPrivyEmail: () => void;
	otpResendSecondsRemaining: number;
}

export default function usePrivyEmailInput({
	email,
	setEmail,
	onLoginWithPrivyEmail,
	otpResendSecondsRemaining,
}: Params) {
	const { sendCode } = useLoginWithEmail();

	const [emailError, setEmailError] = useState('');
	const [isSendingCode, setIsSendingCode] = useState(false);

	const isSendCodeButtonDisabled =
		!email || !EMAIL_VALIDATION_REGEX.test(email) || isSendingCode;

	const handleChange = useCallback((value: string) => {
		setEmail(value);
		setEmailError('');
	}, []);

	const handleBlur = useCallback(() => {
		if (email && !EMAIL_VALIDATION_REGEX.test(email)) {
			setEmailError('Please enter a valid email address');
		} else {
			setEmailError('');
		}
	}, [email]);

	const handleSendCode = useCallback(async () => {
		if (isSendCodeButtonDisabled) {
			return;
		}

		// If a code has been sent and we're within the resend timeout,
		// simply navigate the user to the OTP page again.
		if (otpResendSecondsRemaining > 0) {
			onLoginWithPrivyEmail();

			return;
		}

		try {
			setIsSendingCode(true);

			await sendCode({ email });

			onLoginWithPrivyEmail();

			notify({
				type: 'success',
				message: 'Verification code sent to your email.',
			});
		} catch (error) {
			console.error(error);

			notify({
				type: 'error',
				message: 'Failed to send verification code.',
			});
		} finally {
			setIsSendingCode(false);
		}
	}, [
		isSendCodeButtonDisabled,
		otpResendSecondsRemaining,
		onLoginWithPrivyEmail,
		sendCode,
		email,
	]);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Enter') {
				handleSendCode();
			}
		},
		[handleSendCode]
	);

	return {
		emailError,
		isSendingCode,
		isSendCodeButtonDisabled,
		handleChange,
		handleKeyDown,
		handleBlur,
		handleSendCode,
	};
}
